import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { DatastoreService } from './datastore.service';
import { ReplacerService } from './replacer.service';

import moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { ModalService } from '../components/modal';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ConfigService } from './config.service';
import { v4 as uuidv4 } from 'uuid';
import { B2cService } from './b2c.service';
import { AuthCookieService } from './auth-cookie.service';
import { isOmthService } from '../journey/reschedule/omth-rooms';

/**
 * Whether the booking falls on a Friday or Saturday in Europe/London — the
 * days the First toast / drinks add-ons can't be provided on a standard
 * booking. Bespoke Plus / Bespoke Extra bookings are exempt — use
 * isFirstToastUnavailableForBooking for the full rule.
 */
export function isFirstToastUnavailableDay(datetime: string | null | undefined): boolean {
	if (!datetime) {
		return false;
	}
	const day = moment(datetime).utc().tz('Europe/London').isoWeekday();
	return day === 5 || day === 6;
}

/**
 * Full First toast availability rule (AC: "First toast can be offered to any
 * ceremony in OMTH on a Friday or Saturday IF they have booked bespoke plus
 * or bespoke extra"): blocked on Friday/Saturday UNLESS the ceremony is at
 * OMTH and the booking is Bespoke Plus / Bespoke Extra — i.e. it carries a
 * ceremony-time extension, detected by the same pair our-ceremony's
 * alreadyExtended uses (settings.extended_at or the extension extra-item
 * link flag). globalServiceId is the booked service's catalogue global_id
 * (dataStore.selectedService.global_id), used for the OMTH test.
 */
export function isFirstToastUnavailableForBooking(
	booking: any,
	bookingHasExtensionItem: boolean,
	globalServiceId: number | string | null | undefined,
): boolean {
	const isBespoke = !!booking?.settings?.extended_at || bookingHasExtensionItem;
	if (isBespoke && isOmthService(globalServiceId)) {
		return false;
	}
	return isFirstToastUnavailableDay(booking?.datetime);
}

@Injectable({
	providedIn: 'root',
})
export class CommonFunctionsService {
	public journeyData: BehaviorSubject<any>;
	public subscription!: Subscription;
	public everySecondTimer: Observable<any> = timer(0, 1000);
	private userLoggedIn = new Subject<boolean>();
	private config = new Subject<any>();
	private location = new Subject<any>();
	private meetingRoom = new Subject<boolean>();
	private cardScanInput = new Subject<any>();
	private siteLoaded = '';
	private compLoaded = '';
	private requestError: Subject<any> | null = new Subject<any>();
	private usingB2c = false;

	b2c: B2cService = inject(B2cService);
	authCookie: AuthCookieService = inject(AuthCookieService);
	constructor(
		private apiService: ApiService,
		private dataStore: DatastoreService,
		private modalService: ModalService,
		private configService: ConfigService,
		private router: Router,
		private replacer: ReplacerService
	) {
		this.journeyData = new BehaviorSubject({});
		this.userLoggedIn.next(false);
	}

	setConfig(config: any) {
		this.config.next(config);
	}

	getConfig(): Observable<any> {
		return this.config.asObservable();
	}

	async setCompanyName(params: any) {
		console.log('params', params);
		if (params.site && params.comp) {
			if (this.siteLoaded !== params.site || this.compLoaded !== params.comp) {
				this.siteLoaded = params.site;
				this.compLoaded = params.comp;
				environment.companyName = params.site;
				let token: any = {};
				try {
					token = await this.configService.getToken(
						this.siteLoaded,
						this.compLoaded
					);
				} catch (err) {
					console.log(err);
				}
				// let configs: any = await this.configService.getConfigs()
				// console.log(configs)
				console.log(token.client, this.siteLoaded + ':' + this.compLoaded);
				if (
					token.client === this.siteLoaded + ':' + this.compLoaded &&
					token.token
				) {
					environment.companyName = this.siteLoaded;
					environment.companyId = parseInt(this.compLoaded);
					environment.clientToken = token.token;
					console.log(
						environment.companyName,
						environment.companyId,
						environment.clientToken,
						'loaded'
					);
				} else {
					console.log('failed to load site');
					this.router.navigate([`/${params.site}/${params.comp}/404`]);
				}
				let config: any = await this.configService.getConfig();

				let companyConfig: any = JSON.stringify(config);
				this.setConfig(companyConfig);
				this.dataStore.config = companyConfig;
				this.dataStore.setColors(config);
				// Check for HAF
				if (config.hasOwnProperty('register')) {
					if (config.register.hasOwnProperty('haf') && config.register.haf) {
						this.dataStore.isHAF = true;
						console.log('This is a HAF registration app');
						if (config.register.hasOwnProperty('singleStepChildProcess')) {
							this.dataStore.hafSingleStepChildProcess =
								config.register.singleStepChildProcess;
						}
						if (config.register.hasOwnProperty('maxChildren')) {
							this.dataStore.maxChildren = parseInt(
								config.register.maxChildren
							);
						}
					}
				}
				if (config.hasOwnProperty('myCeremony')) {
					if (config.myCeremony.hasOwnProperty('access')) {
						if (config.myCeremony.access.hasOwnProperty('b2c')) {
							this.usingB2c = true;
						}
					}
				}
			}
		} else {
			this.router.navigate(['/404']);
		}
	}

	getCompanyName() {
		return environment.companyName;
	}

	getCompanyId() {
		return environment.companyId;
	}

	setLocation(location: any) {
		this.location.next(location);
	}

	getLocation(): Observable<any> {
		return this.location.asObservable();
	}

	setMeetingRoom(meetingRoom: boolean) {
		this.meetingRoom.next(meetingRoom);
	}

	getMeetingRoom() {
		return this.meetingRoom.asObservable();
	}

	setUserLoggedIn(userLoggedIn: boolean) {
		this.userLoggedIn.next(userLoggedIn);
	}

	getUserLoggedIn(): Observable<boolean> {
		return this.userLoggedIn.asObservable();
	}

	setJourneyData(data: any) {
		this.journeyData.next(data);
	}

	setCardScanInput(data: any) {
		this.cardScanInput.next(data);
	}

	getCardScanInput(): Observable<any> {
		return this.cardScanInput.asObservable();
	}

	async getBookings() {
		const bookings: any = await this.apiService.getBookings(
			this.dataStore.company.id,
			this.dataStore.member.id
		);
		return bookings['_embedded'].bookings;
	}

	async bookingCancel(id: any) {
		const res: any = await this.apiService.bookingCancel(
			this.dataStore.company.id,
			id,
			true,
			'No show'
		);
		return res;
	}

	async patchMember(member: any) {
		const res: any = await this.apiService.patchMember(
			this.dataStore.company.id,
			member.id,
			member
		);
		return res; //.bookings;
	}

	async getPastBookings() {
		const bookings: any = await this.apiService.getPastBookings(
			this.dataStore.company.id,
			this.dataStore.member.id
		);
		return bookings['_embedded'].bookings;
	}

	async getBookingsAttendees(company_id: number, id: number) {
		const attendees: any = await this.apiService.getBookingsAttendees(
			this.dataStore.company,
			this.dataStore.member.id,
			id
		);
		return attendees['_embedded'].attendees; //.bookings;
	}

	getResource(resourceId: any) {
		let resource = '';
		this.dataStore.resources.forEach(
			(element: { resource_id: any; name: any }) => {
				if (element.resource_id === parseInt(resourceId)) {
					resource = element.name;
					return;
				}
			}
		);

		return resource;
	}

	getParent(company: any) {
		let parentComp = '';

		this.dataStore.companies.forEach((element: { id: any; parent: any }) => {
			if (element.id === parseInt(company)) {
				parentComp = element.parent;
				return;
			}
		});

		return parentComp;
	}

	getBuilding(company: any) {
		if (!this.dataStore.companies) {
			return;
		}

		const parentComp = this.getParent(company);

		let building = '';

		this.dataStore.companies.forEach((element: { id: any; name: any }) => {
			if (element.id === parseInt(parentComp)) {
				building = element.name;
				return;
			}
		});

		return building;
	}

	async checkinBooking(company: any, bookingId: any) {
		await this.apiService.putCheckinBooking(
			company,
			this.dataStore.member.id,
			bookingId
		);
	}

	async checkoutBooking(company: any, bookingId: any) {
		await this.apiService.getCheckoutBooking(
			company,
			this.dataStore.member.id,
			bookingId
		);
	}

	async amendBooking(booking: any, date: any, time: any, duration: any) {
		return await this.apiService.moveBooking(booking, date, time, duration);
	}

	async cancelBooking(company: any, bookingId: any) {
		await this.apiService.getCancelBooking(
			company,
			this.dataStore.member.id,
			bookingId
		);
	}

	disableAction(booking: any, action: string) {
		const service = booking.service_name;
		const timeLimits = this.dataStore.timeLimits;
		const configObj: any = sessionStorage.getItem('config');
		const config: any = JSON.parse(configObj);
		let limit: any;

		const bookingStartTime = moment(booking.datetime).utc().tz('Europe/London');
		const bookingEndTime = moment(booking.end_datetime)
			.utc()
			.tz('Europe/London');

		timeLimits.forEach((timeLimit: any) => {
			if (service === timeLimit.name) {
				limit = timeLimit.limit;
			}
		});

		const bookingChangeLimit = moment(booking.datetime)
			.utc()
			.tz('Europe/London')
			.subtract('minutes', limit);

		const currentTime = moment().utc().tz('Europe/London');

		if (action === 'checkin') {
			const configServiceName = service
				.toLowerCase()
				.replaceAll(' ', '_')
				.trim();
			const minTime = config.checkinRules[configServiceName]?.min_checkin_time;
			const maxTime = config.checkinRules[configServiceName]?.max_checkin_time;

			const checkinStart = moment(booking.datetime).utc().tz('Europe/London');
			const checkinEnd = moment(booking.datetime).utc().tz('Europe/London');
			const checkinStartTime = checkinStart
				.subtract(minTime, 'minutes')
				.format();
			const checkinEndTime = checkinEnd.add(maxTime, 'minutes').format();
			if (currentTime.isBefore(checkinStartTime)) {
				return true;
			}
			if (currentTime.isAfter(checkinEndTime)) {
				return true;
			}

			return false;
		}

		if (action === 'amend') {
			const configServiceName = service
				.toLowerCase()
				.replaceAll(' ', '_')
				.trim();
			const minTime = config.bookingRules[configServiceName]?.min_amend_time;

			if (currentTime.isAfter(bookingStartTime)) {
				return true;
			}

			return false;
		}

		if (action === 'cancel') {
			if (currentTime.isAfter(bookingChangeLimit)) {
				return true;
			}
		}

		return false;
	}

	isLoggedIn() {
		if (localStorage.getItem('auth_token') && localStorage.getItem('member')) {
			return true;
		}

		return false;
	}

	bookingStage(stage: any) {
		if (this.dataStore.bookingDetails?.serviceId === '') {
			return 'service';
		}

		switch (stage) {
			case 'find':
				return '';
			case 'addon':
				if (
					this.dataStore.bookingDetails?.childCompanyId === '' ||
					this.dataStore.bookingDetails?.parentCompanyId === '' ||
					this.dataStore.bookingDetails?.resourceId === ''
				) {
					return 'find';
				}
				return '';
			case 'summary':
				if (
					this.dataStore.bookingDetails?.childCompanyId === '' ||
					this.dataStore.bookingDetails?.parentCompanyId === '' ||
					this.dataStore.bookingDetails?.resourceId === ''
				) {
					return 'find';
				}
				if (this.dataStore.bookingDetails?.addon === '') {
					return 'addon';
				}
				return '';
			default:
				break;
		}
		return 'access';
	}

	newBooking() {
		this.dataStore.bookingDetails = {
			serviceId: '',
			childCompanyId: '',
			parentCompanyId: '',
			resourceId: '',
			resourceName: '',
			event_id: '',
			time: '',
			date: '',
			addon: '',
			attendees: [],
			bookingId: '',
			res: '',
		};
		this.dataStore.serviceItem = '';
		this.dataStore.checkout = '';
		this.dataStore.basket = null;
		this.dataStore.basketEndTime = null;
		this.dataStore.timer = false;
		this.dataStore.multi = false;
		this.dataStore.extraBookings = [];
	}

	async formatQuestions(questions: any) {
		var questionKeys = Object.keys(questions).map((key) => [
			Number(key),
			questions[key],
		]);

		const basketQuestions = [];

		for (const question of questionKeys) {
			if (!isNaN(question[0]) && question[1]) {
				basketQuestions.push({
					id: question[0],
					answer: question[1],
				});
			}
		}

		return basketQuestions;
	}

	getEndTime(booking: any) {
		const endTime = moment(booking.datetime)
			.utc()
			.tz('Europe/London')
			.add(booking.duration, 'seconds');
		return endTime.format();
	}

	/**
	 * Booking durations come back in different units depending on the endpoint:
	 * the single-booking GET returns minutes, the list endpoint returns seconds
	 * (RWRSPI-968). A ceremony is 30–90 minutes, so any value above 300 must be
	 * seconds and is converted; smaller values are already minutes. Use this
	 * anywhere a booking's duration needs to be normalised to minutes rather
	 * than a naive `/ 60`, which silently mangles minutes-valued durations.
	 */
	static bookingDurationToMinutes(duration: any): number {
		const value = Number(duration);
		if (!value || Number.isNaN(value)) return 0;
		return value > 300 ? Math.round(value / 60) : Math.round(value);
	}

	searchQuestion(id: any) {
		const config = sessionStorage.getItem('config');
		if (config) {
			const configJson = JSON.parse(config);
			const serviceId = this.dataStore.bookingDetails.serviceId;
			if (configJson.searchFields.service[`${serviceId}`]?.id === String(id)) {
				return true;
			} else {
				return false;
			}
		}

		return false;
	}

	isKiosk(): boolean {
		// const host = window.location.host;
		// if (host != "localhost:4200" && host != "localhost:4201") {
		//   const path = window.location.pathname;
		//   if (path.indexOf("kiosk") === -1) {
		//     return false;
		//   }
		// }

		const configObj: any = sessionStorage.getItem('config');
		const config: any = JSON.parse(configObj);
		const kioskAvailable = config?.kiosk;

		if (sessionStorage.getItem('kiosk')) {
			const kioskObj: any = sessionStorage.getItem('kiosk');
			const kiosk = JSON.parse(kioskObj);

			const configObj: any = sessionStorage.getItem('config');
			const config: any = JSON.parse(configObj);
			const kioskAvailable = config?.kiosk;

			if (kioskAvailable && kiosk === 'true') {
				return true;
			}
		}

		return false;
	}

	isMeetingRoom(): boolean {
		const path = window.location.pathname;

		if (path.indexOf('meeting-room') === -1) {
			return false;
		}

		const configObj: any = sessionStorage.getItem('config');
		const config: any = JSON.parse(configObj);
		const meetingRoomAvailable = config?.meeting_room;

		if (meetingRoomAvailable) {
			return true;
		}

		if (sessionStorage.getItem('meetingRoom')) {
			const meetingRoomObj: any = sessionStorage.getItem('meetingRoom');
			const meetingRoom = JSON.parse(meetingRoomObj);

			const configObj: any = sessionStorage.getItem('config');
			const config: any = JSON.parse(configObj);
			const meetingRoomAvailable = config?.meeting_room;

			if (meetingRoomAvailable && meetingRoom === 'true') {
				return true;
			}

			sessionStorage.setItem('meetingRoom', JSON.stringify('false'));
			this.setMeetingRoom(false);
		}

		return false;
	}

	signOut() {
		const config = sessionStorage.getItem('config') as string;
		const configObj = JSON.parse(config) as any;
		const canUseSSO = configObj.hasOwnProperty('sso') ? configObj.sso : false;

		if (canUseSSO) {
			this.ssoLogout();
		} else {
			this.logout();
		}
	}

	logout() {
		localStorage.clear();
		sessionStorage.clear();
		this.authCookie.clearLoggedIn();
		this.dataStore.authToken = null;
		this.dataStore.member = null;
		this.dataStore.member = null;
		this.dataStore.loginObject = null;
		this.dataStore.loggedIn = false;
		if (this.usingB2c) {
			this.b2c.removeDecodedToken();
		}
		this.newBooking();
		this.router.navigate([
			`${this.getCompanyName()}/${this.getCompanyId()}/access`,
		]);
	}

	ssoLogout() {
		// Add log out function here
		let url = '/';
		const host = window.location.host;
		if (
			host === 'localhost:4200' ||
			host === 'localhost:4201' ||
			host === 'staging.bookinglab.co.uk' ||
			host === 'bookings.bookinglab.co.uk'
		) {
			const pathname = window.location.pathname;
			var split = pathname.split('/');
			var company = split[1].split('-desk-room-bookings');
			var companyName = company[0];

			if (host === 'staging.bookinglab.co.uk') {
				url = split[1];
			}
			if (host === 'localhost:4201' || host === 'localhost:4200') {
				url = '/';
			} else {
				url = `https://${host}/${split[1]}` as string;
			}
		}

		localStorage.clear();
		sessionStorage.clear();
		this.authCookie.clearLoggedIn();
		this.dataStore.authToken = null;
		this.dataStore.member = null;
		this.dataStore.loggedIn = false;
		if (this.usingB2c) {
			this.b2c.removeDecodedToken();
		}
		this.newBooking();

		// this.authService.logoutRedirect({
		//   postLogoutRedirectUri: `${url}`
		// });
	}

	kioskLocation() {
		const location = sessionStorage.getItem('location') as any;
		const locationObj = JSON.parse(location) as any;

		const building = locationObj.building;

		return building;
	}

	openModal(id: string) {
		this.modalService.open(id);
	}

	closeModal(id: string) {
		this.modalService.close(id);
	}

	scrollToTop() {
		const scrollToTop = window.setInterval(() => {
			const pos = window.pageYOffset;
			if (pos > 0) {
				window.scrollTo(0, pos - 20);
			} else {
				window.clearInterval(scrollToTop);
			}
		}, 10);
	}

	setRequestError(error: any) {
		this.requestError?.next(error);
	}

	getRequestError(): Observable<any> {
		return this.requestError!.asObservable();
	}

	resetConnectionError() {
		this.requestError = null;
	}

	fontChecker() {
		const fonters = document.getElementsByClassName('fonter'); // ever bit of text has this class
		for (let i in fonters) {
			const fonter: any = fonters[i];
			if (fonter.parentElement !== undefined) {
				this.fontUpdate(fonter);
			}
		}
	}

	fontUpdate(fonter: any) {
		// check if the text's span is larger than the div/button/etc.
		// if it is then   we reduce the font size a bit, and check if it now fits
		// if not shrink and check again, and so on till it fits
		// (we add 10% of the parent's size to the child's to ensure the last letter is not clipping through the side of the parent)
		if (
			fonter.getBoundingClientRect().width +
				fonter.parentElement.getBoundingClientRect().width * 0.1 >
			fonter.parentElement.getBoundingClientRect().width
		) {
			const newFontSize =
				parseInt(
					window
						.getComputedStyle(fonter, null)
						.getPropertyValue('font-size')
						.replace('px', '')
				) - 2;
			const oldFontSize = fonter.style.fontSize;
			fonter.style.fontSize = newFontSize + 'px';
			if (newFontSize === oldFontSize) fonter.hidden = false;
			this.fontUpdate(fonter);
		}
	}

	generateHAFId() {
		let uuid = uuidv4();
		const config = JSON.parse(this.dataStore.config);
		if (config.hasOwnProperty('register')) {
			if (config.register.hasOwnProperty('haf') && config.register.haf) {
				this.dataStore.isHAF = true;
				console.log('This is a HAF registration app');
				if (config.register.hasOwnProperty('hafIdPrefix')) {
					uuid = `${config.register.hafIdPrefix}-${uuid}`;
				}
			}
		}
		return uuid;
	}

	checkBoxGroupsCheck(config: any, controls: any, page: string) {
		let allAtLeastOne = true;
		const errorFields = [];
		for (let group of config.myCeremony.checkboxGroups) {
			let groupAtLeastOne = false;
			let groupAllDisabled = true;
			if (group.page === page) {
				for (let box of group.group) {
					// console.log(controls[box])
					if (controls[box].value === true) {
						groupAtLeastOne = true;
					}
					if (controls[box].status !== 'DISABLED') {
						groupAllDisabled = false;
					}
				}
				if (!groupAtLeastOne && !groupAllDisabled) {
					allAtLeastOne = false;
					errorFields.push(group.group[0]);
				}
			}
		}
		return { allAtLeastOne: allAtLeastOne, errorFields: errorFields };
	}

	alreadyLoggedIn() {
		const loggedInMemberData = localStorage.getItem('loginObject');
		if (loggedInMemberData) {
			const loggedInData = JSON.parse(loggedInMemberData);
			const loggedInMember = loggedInData._embedded.members[0];
			this.dataStore.authToken = loggedInData.auth_token;
			this.dataStore.loginObject = loggedInData;
			this.dataStore.member = loggedInMember;
			this.dataStore.loggedIn = true;
			return true;
		}
		return false;
	}

	navigateToFrom(
		to: string,
		from: string,
		queryParams: Record<string, any> = undefined
	) {
		console.log(`setting route to: ${to} from ${from}`);
		this.dataStore.currentPage = from;
		console.log(`${this.getCompanyName()}/${this.getCompanyId()}/${to}`);
		this.dataStore.setCurrentPage(to);
		this.router.navigate(
			[`${this.getCompanyName()}/${this.getCompanyId()}/${to}`],
			{ queryParams }
		);
	}

	async setupCustomObjects(booking: any) {
		if (!booking) {
			const bookingStr = localStorage.getItem('ceremony') as string;
			booking = JSON.parse(bookingStr);
		}
    const serviceStr = localStorage.getItem('service') as string;
    let service;
    if (serviceStr) {
      service = JSON.parse(serviceStr);
    }
		const customObjectsStr = sessionStorage.getItem('customObjects') as any;
		let customObjects = false;
		if (customObjectsStr) {
			customObjects = JSON.parse(customObjectsStr);
			this.dataStore.customObjects = customObjects;
			return this.dataStore.customObjects;
		}
		if (!customObjects) {
			try {
				const ceremonyStr = localStorage.getItem('ceremony') as any;
				if (ceremonyStr) {
					const ceremony = JSON.parse(ceremonyStr);
					const customObjects =
						(await this.apiService.getCustomObjectsForCompany(
							ceremony.company_id,
							'company_cms',
							'company_bullet_maps',
							'company_bullet'
						)) as any[];
					// also get them for the parent...
					const config = JSON.parse(this.dataStore.config);
					const customParentObjects =
						(await this.apiService.getCustomObjectsForCompany(
							config.company,
							'company_cms',
							'company_bullet_maps',
							'company_bullet'
						)) as any[];
					const combinedCustomObjects =
						customObjects.concat(customParentObjects);
					for (const obj of combinedCustomObjects) {
						this.dataStore.customObjects[obj.location] = [];
					}
					for (const obj of combinedCustomObjects) {
						if (obj.slug && obj.location) {
              if (obj.services) {
                const services = obj.services.split(',');
                console.log('found services', services);
                const existingObj = this.dataStore.customObjects[
                  obj.location
                ].find((o: any) => o.id === obj.id);
                if (!existingObj) {
                  console.log(`service includes ${service.global_id}`, services.includes(`${service.global_id}`));
                  if (services.includes(`${service.global_id}`)) {
                    obj.body = await this.replacer.replace(obj.body, booking);
                    this.dataStore.customObjects[obj.location].push(obj);
                  }
                }
              }
              else {
                const existingObj = this.dataStore.customObjects[
                  obj.location
                ].find((o: any) => o.id === obj.id);
                if (!existingObj) {
                  obj.body = await this.replacer.replace(obj.body, booking);
                  this.dataStore.customObjects[obj.location].push(obj);
                }
              }
						}
					}
					console.log(this.dataStore.customObjects);
					sessionStorage.setItem(
						'customObjects',
						JSON.stringify(this.dataStore.customObjects)
					);
				}
			} catch (err) {
				console.error(`Failed to get custom objects`);
				console.error(err);
			}
		}
		return false;
	}
}